import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useInvisibleTurnstile } from '@/hooks/useInvisibleTurnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Display, Heading2, Paragraph } from '@/components/Typography';
import { addToWaitlist } from '@/api/services/waitlist/addToWaitlist';
import { waitlistPageMessages } from './waitlistPage.messages';
import { ApiError } from '@/api/ApiError';
import { AddToWaitlistRequestSchema, type AddToWaitlistRequest } from 'schemas';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';

type WaitlistFormData = Omit<AddToWaitlistRequest, 'captchaToken'>;
const WaitlistFormSchema = AddToWaitlistRequestSchema.omit({
  captchaToken: true,
});

export function WaitlistPage() {
  const [isSuccess, setIsSuccess] = useState(false);
  const { Widget: Captcha, getToken, reset } = useInvisibleTurnstile();

  const form = useForm<WaitlistFormData>({
    resolver: zodResolver(WaitlistFormSchema),
    defaultValues: {
      email: '',
      consentToDataProcessing: false,
    },
  });

  const onSubmit = async (data: WaitlistFormData) => {
    try {
      const captchaToken = await getToken();
      await addToWaitlist({ ...data, captchaToken });
      reset();
      setIsSuccess(true);
    } catch (err) {
      reset();
      const apiError = err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
      toast.error(apiError.message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-200 via-pink-200 to-orange-200 p-4">
      <div className="w-full max-w-6xl text-center">
        {isSuccess ? (
          <>
            <div className="mx-auto my-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <Heading2 className="mt-6">
              {waitlistPageMessages.successTitle}
            </Heading2>
            <Paragraph className="my-6">
              {waitlistPageMessages.successMessage}
            </Paragraph>
          </>
        ) : (
          <>
            {/* Logo */}
            <div className="mx-auto mb-8 flex h-72 items-center justify-center lg:mb-10">
              <img
                src="/logo/wfsc-logo.png"
                alt="Warm Fuzzy Story Club Logo"
                className="h-full w-full object-contain"
                onError={(e) => {
                  // Fallback to text if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML =
                      '<div class="flex h-full w-full items-center justify-center rounded-full bg-primary text-2xl font-bold text-white">WFSC</div>';
                  }
                }}
              />
            </div>

            <Display className="mb-16">{waitlistPageMessages.title}</Display>
            <Paragraph className="mx-auto mt-8 w-full max-w-xl">
              {waitlistPageMessages.description}
            </Paragraph>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="mx-auto mt-8 flex w-full max-w-lg flex-col items-center space-y-2"
              >
                <div className="w-full">
                  <FormLabel className="sr-only">
                    {waitlistPageMessages.emailLabel}
                  </FormLabel>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={
                                waitlistPageMessages.emailPlaceholder
                              }
                              disabled={form.formState.isSubmitting}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      disabled={
                        form.formState.isSubmitting || !form.formState.isValid
                      }
                      className="w-full min-w-[160px] px-6 sm:w-auto"
                    >
                      {form.formState.isSubmitting
                        ? waitlistPageMessages.submittingButton
                        : waitlistPageMessages.submitButton}
                    </Button>
                  </div>
                </div>

                <div className="flex w-full justify-center">
                  <Captcha />
                </div>

                {/* Consent Checkbox */}
                <FormField
                  control={form.control}
                  name="consentToDataProcessing"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-1 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={form.formState.isSubmitting}
                          className="bg-white"
                        />
                      </FormControl>
                      <div className="leading-none">
                        <FormLabel className="text-xs font-normal text-stone-600">
                          {waitlistPageMessages.consentText}
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}
